import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Textinput from "@/components/ui/Textinput";
import Icon from "@/components/ui/Icon";
import Button from "@/components/ui/Button";
import { useForm, useFieldArray } from "react-hook-form";
import {useTranslation} from "react-i18next";

const defaultMessage = "Let us know how to contact you";

const LeadFields = forwardRef((props, ref) => {
  const {t} = useTranslation();

  let initFields = props.fields;
  if (!initFields) {
    initFields = [
      { title: "Name", enabled: false },
      { title: "Email", enabled: false },
      { title: "Phone number", enabled: false },
    ];
  }

  const [message, setMessage] = useState(
    props.message ? props.message : defaultMessage,
  );

  const { register, control, handleSubmit, reset, trigger, setError } = useForm(
    {
      defaultValues: {
        fields: initFields,
      },
    },
  );
  const { fields, append, remove } = useFieldArray({
    control,
    name: "fields",
  });
  const index = 1;
  const submitBtnRef = useRef();

  let resolver = null;
  useImperativeHandle(ref, () => ({
    doSomething: (event) => {
      return new Promise((resolve, reject) => {
        submitBtnRef.current.click();
        resolver = resolve;
      });
    },
  }));

  const saveHandler = async (data) => {
    let arr = [];
    for (let i = 0; i < data.fields.length; i++) {
      arr.push(data.fields[i]);
    }
    data.fields = arr;
    data.message = message;
    await props.onSave(data, resolver);
  };

  return (
    <div>
      <div className="flex justify-between items-end">
        <div className="w-1/2">
          <Textinput
            label={t("title")}
            type="text"
            placeholder="Ask visitor to fill in data"
            defaultValue={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="border-2 rounded-lg p-4 mt-6">
        <div className="flex justify-between">
          <div className="font-bold mb-4">{t("fields")}:</div>
          <div>
            <Button
              text={t("add")}
              className="btn-outline-primary btn-sm"
              onClick={() => append()}
            />
          </div>
        </div>
        <form onSubmit={handleSubmit((data) => saveHandler(data))}>
          {fields.map((item, index) => (
            <div
              className="lg:grid-cols-3 md:grid-cols-2 grid-cols-1 grid gap-5 mb-5 last:mb-0"
              key={index}
            >
              <Textinput
                type="text"
                id={`name${index}`}
                placeholder="Field name (e.g Name/Email/Phone)"
                register={register}
                name={`fields[${index}].title`}
              />

              <div className="flex items-end space-x-3">
                <div className="mb-1.5 mr-6">
                  <input
                    className="mr-2 mt-[0.3rem] h-3.5 w-8 appearance-none rounded-[0.4375rem] bg-neutral-300 before:pointer-events-none before:absolute before:h-3.5 before:w-3.5 before:rounded-full before:bg-transparent before:content-[''] after:absolute after:z-[2] after:-mt-[0.1875rem] after:h-5 after:w-5 after:rounded-full after:border-none after:bg-neutral-100 after:shadow-[0_0px_3px_0_rgb(0_0_0_/_7%),_0_2px_2px_0_rgb(0_0_0_/_4%)] after:transition-[background-color_0.2s,transform_0.2s] after:content-[''] checked:bg-primary-500-500 checked:after:absolute checked:after:z-[2] checked:after:-mt-[3px] checked:after:ml-[1.0625rem] checked:after:h-5 checked:after:w-5 checked:after:rounded-full checked:after:border-none checked:after:bg-primary-500 checked:after:shadow-[0_3px_1px_-2px_rgba(0,0,0,0.2),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)] checked:after:transition-[background-color_0.2s,transform_0.2s] checked:after:content-[''] hover:cursor-pointer focus:outline-none focus:ring-0 focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[3px_-1px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-5 focus:after:w-5 focus:after:rounded-full focus:after:content-[''] checked:focus:border-primary checked:focus:bg-primary-500 checked:focus:before:ml-[1.0625rem] checked:focus:before:scale-100 checked:focus:before:shadow-[3px_-1px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] dark:bg-neutral-600 dark:after:bg-neutral-400 dark:checked:bg-primary-500 dark:checked:after:bg-primary-500 dark:focus:before:shadow-[3px_-1px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[3px_-1px_0px_13px_#3b71ca]"
                    type="checkbox"
                    role="switch"
                    id={`enabled${index}`}
                    name={`fields[${index}].enabled`}
                    {...register(`fields[${index}].enabled`)}
                  />
                  <label
                    className="inline-block pl-[0.15rem] hover:cursor-pointer"
                    htmlFor={`enabled${index}`}
                  >
                    {t("enabled")}
                  </label>
                </div>

                <button
                  onClick={() => remove(index)}
                  type="button"
                  className="inline-flex items-center justify-center h-10 w-10 border-danger-500 text-lg border rounded text-danger-500"
                >
                  <Icon icon="heroicons-outline:trash" />
                </button>
              </div>
            </div>
          ))}

          <input ref={submitBtnRef} type="submit" hidden={true}></input>
        </form>
      </div>
    </div>
  );
});

export default LeadFields;
